import _ from 'underscore';
import React, { useContext, Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Text, FontIcon, Image } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';

import { BulletItem } from './bullet-item';
import { getModeItemStyles, modeItemStyles } from './styles';
import type { AccountModeBullet, ModeItemProps } from '../types';

export const ModeItem = (props: ModeItemProps) => {
  const {
    selected,
    onClick,
    item,
    isRecommended,
    displayMode,
    isRebranding,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getModeItemStyles);
  const {
    name, imgSrc, imgAlt, title, description, bulletsTitle, bulletItems,
  } = item;

  return (
    <div className={classNames(classes.itemContainer, 'item-container', classes[`itemContainer-${displayMode}`])}>
      <div className={classNames(classes.recommended, isRecommended && !selected && classes.recommendedNotSelected, !isRecommended && classes.recommendedNotVisible)}>
        {i18n.getString(_TL_('Recommended'))}
      </div>
      <button
        role="radio"
        aria-checked={selected ? 'true': 'false'}
        className={classNames(
          `${name}-option`,
          classes.item,
          selected ? classes.optionSelected : classes.optionNotSelected,
          selected && 'selected',
          isRecommended && classes.itemRecommended,
          'item'
        )}
        onClick={onClick}
      >
        <Image
          src={imgSrc}
          alt={i18n.getString(imgAlt)}
          styles={modeItemStyles.image}
          aria-hidden="true"
        />
        <Text
          variant="medium"
          block
          styles={modeItemStyles.title}
        >
          {i18n.getString(title)}
        </Text>
        {selected && <FontIcon aria-label="Location" iconName="Location" className={classes.icon} />}
        <Text
          variant="medium"
          block
          className={classNames(
            classes.description,
            isRebranding && selected && classes.rebrandingSelectedDescription,
            isRebranding && !selected && classes.rebrandingNotSelectedDescription
          )}
        >
          {i18n.getString(description)}
        </Text>
        {!_.isEmpty(bulletItems) &&
          <Fragment>
            <div className={classNames('horizontal-divider', classes.divider)} />
            <Text
              variant="medium"
              block
              styles={modeItemStyles.bulletsTitle}
            >
              {i18n.getString(bulletsTitle)}
            </Text>
            {!isRebranding &&
              <ul>
                {bulletItems.map((bulletItem: string | AccountModeBullet) => {
                  const text = _.isString(bulletItem) ? bulletItem : bulletItem.text;
                  return (
                    <li className="bullet-item">
                      <Text
                        variant="medium"
                        block
                        styles={modeItemStyles.text}
                      >
                        <ReactTemplate
                          model={{ bold: (t: React.ReactNode) => <b>{t}</b> }}
                          template={i18n.getString(text)}
                        />
                      </Text>
                    </li>
                  );
                })}
              </ul>
            }
            {isRebranding &&
              <div className={classNames('bullet-list')}>
                {bulletItems.map((bulletItem: AccountModeBullet) => {
                  const { key, text, renderIcon } = bulletItem;
                  return (
                    <BulletItem key={key} text={text} renderIcon={renderIcon} />
                  );
                })}
              </div>
            }
          </Fragment>
        }
      </button>
    </div>
  );
};

ModeItem.propTypes = {
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    imgSrc: PropTypes.string.isRequired,
    imgAlt: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    bulletsTitle: PropTypes.string,
    bulletItems: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
    ])),
  }).isRequired,
  isRecommended: PropTypes.bool,
  isRebranding: PropTypes.bool,
  displayMode: PropTypes.string,
};

ModeItem.defaultProps = {
  isRecommended: false,
  isRebranding: false,
  displayMode: '',
};
